import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const SetupCommunication = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState('email-config');

  // Data states
  const [commSettings, setCommSettings] = useState({});
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [backupLogs, setBackupLogs] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateType, setTemplateType] = useState('email');

  // Color palette
  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  // Responsive breakpoints
  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const settingsData = await apiGet('/admin/setup/communication/settings');
      if (settingsData.success) setCommSettings(settingsData.data);

      if (activeTab === 'email-templates') {
        const templatesData = await apiGet('/admin/setup/communication/email-templates');
        if (templatesData.success) setEmailTemplates(templatesData.data);
      }

      if (activeTab === 'sms-templates') {
        const templatesData = await apiGet('/admin/setup/communication/sms-templates');
        if (templatesData.success) setSmsTemplates(templatesData.data);
      }

      if (activeTab === 'backup') {
        const logsData = await apiGet('/admin/setup/communication/backup-logs');
        if (logsData.success) setBackupLogs(logsData.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const handleUpdateSetting = async (key, value) => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/setup/communication/settings/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value })
      });
      const data = await response.json();
      if (data.success) {
        alert('✅ Setting updated successfully!');
        fetchData();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating setting:', error);
      alert('Error updating setting');
    }
  };

  const handleTestEmail = async () => {
    const testEmail = window.prompt('Enter test email address:');
    if (!testEmail) return;

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/setup/communication/test-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test_email: testEmail })
      });
      const data = await response.json();
      if (data.success) {
        alert('✅ ' + data.message);
      } else {
        alert('❌ Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error testing email:', error);
      alert('Error sending test email');
    }
  };

  const handleTestSMS = async () => {
    const testPhone = window.prompt('Enter test phone number:');
    if (!testPhone) return;

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/setup/communication/test-sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test_phone: testPhone })
      });
      const data = await response.json();
      if (data.success) {
        alert('✅ ' + data.message);
      } else {
        alert('❌ Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error testing SMS:', error);
      alert('Error sending test SMS');
    }
  };

  const handleSaveTemplate = async (formData) => {
    try {
      const url = templateType === 'email'
        ? `${PRODUCTION_URL}/api/admin/setup/communication/email-templates/${selectedTemplate.id}`
        : `${PRODUCTION_URL}/api/admin/setup/communication/sms-templates/${selectedTemplate.id}`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        alert('✅ Template updated successfully!');
        setShowTemplateModal(false);
        setSelectedTemplate(null);
        fetchData();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Error saving template');
    }
  };

  const handleCreateBackup = async () => {
    if (!window.confirm('Create a new database backup? This may take a few moments.')) return;

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/setup/communication/backup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        alert('✅ Backup created successfully!');
        fetchData();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error creating backup:', error);
      alert('Error creating backup');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    pageSubtitle: {
      fontSize: isMobile ? '12px' : '14px',
      color: colors.textLight
    },
    tabContainer: {
      display: 'flex',
      gap: isMobile ? '8px' : '12px',
      marginBottom: isMobile ? '16px' : '20px',
      padding: isMobile ? '0 4px' : '0',
      flexWrap: 'wrap'
    },
    tab: (active) => ({
      padding: isMobile ? '10px 14px' : '12px 20px',
      background: active ? colors.primary : colors.white,
      color: active ? colors.white : colors.textDark,
      border: `2px solid ${active ? colors.primary : colors.mediumGray}`,
      borderRadius: '10px',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s'
    }),
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '18px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px' : '0'
    },
    formGroup: {
      marginBottom: isMobile ? '16px' : '20px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      color: colors.textDark
    },
    input: {
      width: '100%',
      padding: isMobile ? '10px' : '12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: isMobile ? '13px' : '14px',
      boxSizing: 'border-box'
    },
    button: {
      padding: isMobile ? '10px 16px' : '12px 20px',
      background: colors.primary,
      color: colors.white,
      border: 'none',
      borderRadius: '8px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      cursor: 'pointer'
    }
  };

  const TemplateModal = () => {
    const [formData, setFormData] = useState(
      templateType === 'email'
        ? {
          subject: selectedTemplate?.subject || '',
          body: selectedTemplate?.body || '',
          is_active: selectedTemplate?.is_active || 1
        }
        : {
          message: selectedTemplate?.message || '',
          is_active: selectedTemplate?.is_active || 1
        }
    );

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
        <div style={{
          background: colors.white,
          padding: isMobile ? '20px' : '30px',
          borderRadius: '16px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxSizing: 'border-box'
        }}>
          <h3 style={{ marginBottom: '20px', fontSize: isMobile ? '18px' : '22px' }}>
            Edit {selectedTemplate?.template_name}
          </h3>

          <div style={{ display: 'grid', gap: '16px' }}>
            {templateType === 'email' && (
              <div>
                <label style={styles.label}>Subject</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  style={styles.input}
                />
              </div>
            )}

            <div>
              <label style={styles.label}>{templateType === 'email' ? 'Body' : 'Message'}</label>
              <textarea
                value={templateType === 'email' ? formData.body : formData.message}
                onChange={(e) => setFormData({
                  ...formData,
                  [templateType === 'email' ? 'body' : 'message']: e.target.value
                })}
                rows={templateType === 'email' ? 12 : 4}
                style={{ ...styles.input, resize: 'vertical', fontFamily: 'monospace' }}
              />
            </div>

            <div style={{
              background: colors.lightGray,
              padding: '12px',
              borderRadius: '8px',
              fontSize: '12px'
            }}>
              <strong>Available Variables:</strong>
              <div style={{ marginTop: '6px', color: colors.textLight }}>
                {selectedTemplate?.variables && JSON.parse(selectedTemplate.variables).join(', ')}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                checked={formData.is_active === 1}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                id="is_active"
              />
              <label htmlFor="is_active" style={{ fontSize: '14px', fontWeight: '600' }}>
                Active
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setShowTemplateModal(false);
                setSelectedTemplate(null);
              }}
              style={{
                padding: '10px 20px',
                background: colors.mediumGray,
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button onClick={() => handleSaveTemplate(formData)} style={styles.button}>
              Save Template
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading settings...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page Header */}
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Communication Setup</h1>
        <p style={styles.pageSubtitle}>
          {isMobile ? 'Templates & backup' : 'Configure email templates, communication settings, and data backup'}
        </p>
      </div>

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button style={styles.tab(activeTab === 'email-config')} onClick={() => setActiveTab('email-config')}>
          📧 Email Config
        </button>
        <button style={styles.tab(activeTab === 'sms-config')} onClick={() => setActiveTab('sms-config')}>
          💬 SMS Config
        </button>
        <button style={styles.tab(activeTab === 'email-templates')} onClick={() => setActiveTab('email-templates')}>
          📝 Email Templates
        </button>
        <button style={styles.tab(activeTab === 'sms-templates')} onClick={() => setActiveTab('sms-templates')}>
          📱 SMS Templates
        </button>
        <button style={styles.tab(activeTab === 'backup')} onClick={() => setActiveTab('backup')}>
          💾 Backup
        </button>
      </div>

      {/* Content */}
      <div style={styles.contentCard}>
        {/* Email Configuration Tab */}
        {activeTab === 'email-config' && commSettings.email && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '700' }}>Email Server Settings</h3>
              <button onClick={handleTestEmail} style={styles.button}>
                📧 Test Email
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? '16px' : '20px' }}>
              {commSettings.email.map(setting => (
                <div key={setting.id} style={styles.formGroup}>
                  <label style={styles.label}>{setting.setting_key.replace(/_/g, ' ').toUpperCase()}</label>
                  <input
                    type={setting.setting_key.includes('password') ? 'password' : 'text'}
                    value={setting.setting_value || ''}
                    onChange={(e) => {
                      const newSettings = { ...commSettings };
                      const settingIndex = newSettings.email.findIndex(s => s.setting_key === setting.setting_key);
                      if (settingIndex !== -1) {
                        newSettings.email[settingIndex].setting_value = e.target.value;
                        setCommSettings(newSettings);
                      }
                    }}
                    onBlur={() => handleUpdateSetting(setting.setting_key, setting.setting_value)}
                    style={styles.input}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SMS Configuration Tab */}
        {activeTab === 'sms-config' && commSettings.sms && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '700' }}>SMS Gateway Settings</h3>
              <button onClick={handleTestSMS} style={styles.button}>
                💬 Test SMS
              </button>
            </div>

            <div style={{ display: 'grid', gap: isMobile ? '16px' : '20px' }}>
              {commSettings.sms.map(setting => (
                <div key={setting.id} style={styles.formGroup}>
                  <label style={styles.label}>{setting.setting_key.replace(/_/g, ' ').toUpperCase()}</label>
                  <input
                    type={setting.setting_key.includes('secret') ? 'password' : 'text'}
                    value={setting.setting_value || ''}
                    onChange={(e) => {
                      const newSettings = { ...commSettings };
                      const settingIndex = newSettings.sms.findIndex(s => s.setting_key === setting.setting_key);
                      if (settingIndex !== -1) {
                        newSettings.sms[settingIndex].setting_value = e.target.value;
                        setCommSettings(newSettings);
                      }
                    }}
                    onBlur={() => handleUpdateSetting(setting.setting_key, setting.setting_value)}
                    style={styles.input}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Email Templates Tab */}
        {activeTab === 'email-templates' && (
          <div>
            <h3 style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '700', marginBottom: '20px' }}>
              Email Templates
            </h3>

            <div style={{ display: 'grid', gap: '16px' }}>
              {emailTemplates.map(template => (
                <div
                  key={template.id}
                  style={{
                    padding: isMobile ? '16px' : '20px',
                    background: colors.lightGray,
                    borderRadius: '12px',
                    border: `1px solid ${colors.mediumGray}`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <strong style={{ fontSize: isMobile ? '15px' : '16px' }}>{template.template_name}</strong>
                        {template.is_active ? (
                          <span style={{
                            padding: '2px 8px',
                            background: colors.success,
                            color: colors.white,
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: '600'
                          }}>
                            ACTIVE
                          </span>
                        ) : (
                          <span style={{
                            padding: '2px 8px',
                            background: colors.textLight,
                            color: colors.white,
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: '600'
                          }}>
                            INACTIVE
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: isMobile ? '12px' : '13px', color: colors.textLight }}>
                        Subject: {template.subject}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedTemplate(template);
                        setTemplateType('email');
                        setShowTemplateModal(true);
                      }}
                      style={{
                        padding: '8px 16px',
                        background: colors.primary,
                        color: colors.white,
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Edit Template
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SMS Templates Tab */}
        {activeTab === 'sms-templates' && (
          <div>
            <h3 style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '700', marginBottom: '20px' }}>
              SMS Templates
            </h3>

            <div style={{ display: 'grid', gap: '16px' }}>
              {smsTemplates.map(template => (
                <div
                  key={template.id}
                  style={{
                    padding: isMobile ? '16px' : '20px',
                    background: colors.lightGray,
                    borderRadius: '12px',
                    border: `1px solid ${colors.mediumGray}`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <strong style={{ fontSize: isMobile ? '15px' : '16px' }}>{template.template_name}</strong>
                        {template.is_active ? (
                          <span style={{
                            padding: '2px 8px',
                            background: colors.success,
                            color: colors.white,
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: '600'
                          }}>
                            ACTIVE
                          </span>
                        ) : (
                          <span style={{
                            padding: '2px 8px',
                            background: colors.textLight,
                            color: colors.white,
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: '600'
                          }}>
                            INACTIVE
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: isMobile ? '12px' : '13px', color: colors.textLight }}>
                        {template.message}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedTemplate(template);
                        setTemplateType('sms');
                        setShowTemplateModal(true);
                      }}
                      style={{
                        padding: '8px 16px',
                        background: colors.primary,
                        color: colors.white,
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Edit Template
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Backup Tab */}
        {activeTab === 'backup' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <h3 style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '700' }}>Database Backups</h3>
              <button onClick={handleCreateBackup} style={styles.button}>
                💾 Create Backup
              </button>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '12px' : '13px' }}>
                <thead>
                  <tr>
                    <th style={{
                      background: colors.lightGray,
                      padding: isMobile ? '10px' : '12px',
                      textAlign: 'left',
                      fontWeight: '700',
                      borderBottom: `2px solid ${colors.mediumGray}`
                    }}>
                      Backup Name
                    </th>
                    <th style={{
                      background: colors.lightGray,
                      padding: isMobile ? '10px' : '12px',
                      textAlign: 'left',
                      fontWeight: '700',
                      borderBottom: `2px solid ${colors.mediumGray}`
                    }}>
                      Size
                    </th>
                    {!isMobile && (
                      <th style={{
                        background: colors.lightGray,
                        padding: '12px',
                        textAlign: 'left',
                        fontWeight: '700',
                        borderBottom: `2px solid ${colors.mediumGray}`
                      }}>
                        Status
                      </th>
                    )}
                    <th style={{
                      background: colors.lightGray,
                      padding: isMobile ? '10px' : '12px',
                      textAlign: 'left',
                      fontWeight: '700',
                      borderBottom: `2px solid ${colors.mediumGray}`
                    }}>
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {backupLogs.map(log => (
                    <tr key={log.id}>
                      <td style={{
                        padding: isMobile ? '10px' : '12px',
                        borderBottom: `1px solid ${colors.lightGray}`
                      }}>
                        {log.backup_name}
                      </td>
                      <td style={{
                        padding: isMobile ? '10px' : '12px',
                        borderBottom: `1px solid ${colors.lightGray}`
                      }}>
                        {formatBytes(log.backup_size)}
                      </td>
                      {!isMobile && (
                        <td style={{
                          padding: '12px',
                          borderBottom: `1px solid ${colors.lightGray}`
                        }}>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            background: log.status === 'success' ? `${colors.success}20` : `${colors.danger}20`,
                            color: log.status === 'success' ? colors.success : colors.danger
                          }}>
                            {log.status}
                          </span>
                        </td>
                      )}
                      <td style={{
                        padding: isMobile ? '10px' : '12px',
                        borderBottom: `1px solid ${colors.lightGray}`
                      }}>
                        {new Date(log.created_at).toLocaleString('en-IN')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showTemplateModal && <TemplateModal />}
    </AdminLayout>
  );
};

export default SetupCommunication;